// src/components/RmsStopBanner.tsx
//
// 楽天RMSにログインできず取得が止まっている間、楽天表示中の全ページ上部に赤い警告を出し続ける（RN-283）。
// 閉じるボタンは置かない（警告が消えない限り数字が更新されないことが、見て分かるようにする）。
// 初期設定の画面は、同じ内容の注意書きを画面の中に出すので、ここでは出さない。
// RN-289: 止まっていないときは、最後にデータを取れた日時を小さく出す（RMS_FETCH_DELAY_HOURS＝30時間を超えて取れていないときだけ薄いオレンジ）。
// 出す・出さないの決め方は YahooReauthBanner にそろえる。見た目は初期設定の画面の知らせと同じ赤の帯（ほかのお知らせより目立たせる）。

import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useMallContext } from "../contexts/MallContext";
import { useUserContext } from "../contexts/UserContext";
import { apiGet } from "../utils/apiClient";
import {
    RmsStopStatus,
    buildRmsStopParts,
    formatRmsLastOk,
    isRmsFetchDelayed,
    parseRmsStopStatus,
    readCachedRmsStopStatus,
    rmsStopCanFixInSettings,
    writeCachedRmsStopStatus,
} from "../utils/rmsStop";

export default function RmsStopBanner({ onNavigateToSettings }: { onNavigateToSettings: () => void }) {
    const { mall } = useMallContext();
    const user = useUserContext();
    const location = useLocation();
    // 前に取れた状態から出す（画面を移るたびに作り直されても、警告が一度消えて出直さない）
    const [status, setStatus] = useState<RmsStopStatus | null>(() => readCachedRmsStopStatus(user.companyId));

    const isDemo = location.pathname.startsWith("/demo");
    const isSettings = location.pathname.includes("/settings");
    const isRakuten = mall === "rakuten";

    // ページを移るたびに取り直す（初期設定で保存したあと、次のページで消えるように）
    useEffect(() => {
        // 初期設定の画面では出さないので取りにも行かない（保存の前の状態を控えに書かないため）
        if (isDemo || !isRakuten || !user.companyId || isSettings) {
            setStatus(null);
            return;
        }
        // 会社を切り替えたときは、その会社の控えから出し直す
        setStatus(readCachedRmsStopStatus(user.companyId));
        let cancelled = false;
        (async () => {
            try {
                const res = await apiGet("/api/company/datafetch-status");
                if (!res.ok) {
                    // 取れないときは出さず、控えも捨てる（古い警告がちらつかないように）
                    if (!cancelled) {
                        setStatus(null);
                        writeCachedRmsStopStatus(user.companyId, null);
                    }
                    return;
                }
                const json = await res.json();
                if (cancelled) return;
                const next = parseRmsStopStatus(json);
                setStatus(next);
                writeCachedRmsStopStatus(user.companyId, next);
            } catch {
                // 取れないときは出さない（初期設定の経由と注意書きで知らせる）。控えも捨てる
                if (!cancelled) {
                    setStatus(null);
                    writeCachedRmsStopStatus(user.companyId, null);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isDemo, isRakuten, isSettings, user.companyId, location.pathname]);

    if (isDemo || !isRakuten || isSettings || !status) return null;

    // RN-289: 止まっていないときは、最後にデータを取れた日時を小さく出す（数字がいつの時点のものか分かるように）。
    //   ふだんは薄い灰色の1行（静かに）。30時間（RMS_FETCH_DELAY_HOURS）を超えて取れていないときだけ、薄いオレンジで知らせる。
    //   止まっているとき（下の赤い警告）は、こちらは出さない（重ねない）。日時が無いときは何も出さない
    //   文に取得の時刻は書かない（有料と無料で取得の回数・時刻が違い、無料の時刻は店ごとにずれるため）。
    //   🔴 SettingsSupportInfo.tsx の「反映の目安」の文は時間割（docs/scheduled-jobs.md）に合わせてある。時間割を変えたら直す
    if (!status.stopped) {
        const label = formatRmsLastOk(status.lastOkAt);
        if (!label) return null;
        if (isRmsFetchDelayed(status.lastOkAt)) {
            return (
                <div role="status" className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-[13px] text-amber-900">
                    <div>楽天からのデータの取得が遅れています。表示中の数字は、最後のデータ取得時点（{label}）のものです。</div>
                    <div>毎日自動で取得し直しており、取得次第反映されます。</div>
                </div>
            );
        }
        return (
            <div className="-mt-2 mb-2 text-right text-[11px] text-slate-400">
                データ取得：{label}
            </div>
        );
    }

    // 初期設定の画面の知らせと同じ形（赤の帯に白の太字の1文目＋薄い赤の2文目）。ほかの案内やお知らせより目立たせる
    const parts = buildRmsStopParts(status);
    return (
        <div role="alert" className="mb-4 overflow-hidden rounded-xl border-2 border-red-600 shadow-md">
            <div className="flex items-center justify-between gap-3 bg-red-600 px-4 py-2 text-white">
                <div className="flex items-start gap-2">
                    <span aria-hidden="true" className="text-[15px] leading-6">⚠</span>
                    <span className="text-[15px] font-bold leading-6">{parts.headline}</span>
                </div>
                {/* RN-311 段2: 初期設定で直せない止まり方（利用者の停止）では出さない（連れて行っても直せないため） */}
                {rmsStopCanFixInSettings(status) && (
                    <button
                        type="button"
                        onClick={onNavigateToSettings}
                        className="shrink-0 rounded bg-white px-3 py-1 text-xs font-bold text-red-700 hover:bg-red-50"
                    >
                        初期設定を開く
                    </button>
                )}
            </div>
            <div className="bg-red-50 px-4 py-2 pl-10 text-[13px] text-red-900">{parts.body}</div>
        </div>
    );
}
